import React, { Suspense, Fragment, lazy } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import LoadingScreen from "src/components/LoadingScreen";
import AuthGuard from "src/components/AuthGuard";
import GuestGuard from "src/components/GuestGuard";
import MainLayout from "src/layouts/MainLayout";
import { ROUTER_NAME } from "src/constants";
import { ACCOUNT_TYPES } from "./constants/AccountTypes";
import PermissionGuard from "./components/PermissionGuard";

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={props => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <PermissionGuard permissionAllow={route.permissionAllow}>
                      <Component {...props} />
                    </PermissionGuard>
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes = [
  {
    exact: true,
    path: ROUTER_NAME.NOT_FOUND,
    component: lazy(() => import("src/views/Errors/NotFoundView"))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: ROUTER_NAME.LOGIN,
    component: lazy(() => import("src/views/Login"))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: ROUTER_NAME.REGISTER,
    component: lazy(() => import("src/views/Register"))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: ROUTER_NAME.FORGOT_PASSWORD,
    component: lazy(() => import("src/views/ForgotPassword"))
  },
  {
    exact: true,
    path: ROUTER_NAME.RESET_PASSWORD,
    component: lazy(() => import("src/views/ResetPassword"))
  },
  {
    path: ROUTER_NAME.ALL,
    guard: AuthGuard,
    layout: MainLayout,
    routes: [
      {
        exact: true,
        path: ROUTER_NAME.DEFAULT,
        component: lazy(() => import("src/views/MapLayer")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.TOOL,
        component: lazy(() => import("src/views/ToolManagement")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.TOOL_TYPE,
        component: lazy(() => import("src/views/ToolTypeManagement")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.MAP_LAYER,
        component: lazy(() => import("src/views/MapLayer")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.MAP_DOCUMENT,
        component: lazy(() => import("src/views/MapManagement")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.DOCUMENT,
        component: lazy(() => import("src/views/DocumentView")),
        permissionAllow: [
          ACCOUNT_TYPES.SYSTEM_ADMIN,
          ACCOUNT_TYPES.LOCAL_GOVERNMENT
        ]
      },
      {
        exact: true,
        path: ROUTER_NAME.NEWS,
        component: lazy(() => import("src/views/News")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.MANAGE_LAND_PRICE,
        component: lazy(() => import("src/views/ManageLandPrice")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN, ACCOUNT_TYPES.VIP_MEMBER]
      },
      {
        exact: true,
        path: ROUTER_NAME.USER_MANAGEMENT,
        component: lazy(() => import("src/views/UserManagement")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.GUIDE,
        component: lazy(() => import("src/views/Guide")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.TERM,
        component: lazy(() => import("src/views/TermView")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.FEEDBACK,
        component: lazy(() => import("src/views/Feedback")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.BASE_MAP_SETTING,
        component: lazy(() => import("src/views/BaseMapSettingView")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.NORMAL_INFO,
        component: lazy(() => import("src/views/NormalInfoView")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.FEEDBACK_INFO,
        component: lazy(() => import("src/views/FeedbackInfoView")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        exact: true,
        path: ROUTER_NAME.UPDATE_GOVERNMENT_PRICE,
        component: lazy(() => import("src/views/UpdateGovernmentPrice")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN, ACCOUNT_TYPES.VIP_MEMBER]
      },
      {
        exact: true,
        path: ROUTER_NAME.UPDATE_INVESTIGATE_PRICE,
        component: lazy(() => import("src/views/UpdateInvestigatePrice")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN, ACCOUNT_TYPES.VIP_MEMBER]
      },
      {
        exact: true,
        path: ROUTER_NAME.WARDS_MANAGE,
        component: lazy(() => import("src/views/Wards")),
        permissionAllow: [ACCOUNT_TYPES.SYSTEM_ADMIN]
      },
      {
        component: () => <Redirect to={ROUTER_NAME.NOT_FOUND} />
      }
    ]
  }
];

export default routes;
